import {
    DocumentType,
    modelOptions,
    PropType,
    plugin,
    post,
    pre,
    prop,
    type Ref,
    Severity,
} from "@typegoose/typegoose";
import mongoose from "mongoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { DeviceTypeConfigurations } from "../../Configurations/DeviceTypeConfiguration/DeviceTypeConfigurations.js";
import { IdentifierSchemeConfigurations } from "../../Configurations/IdentifierSchemeConfiguration/IdentifierSchemeConfigurations.js";
import { ThirdPartyDeviceType } from "../../DeviceTypes/ThirdPartyDevice.js";
import { RootLogger } from "../../logging/index.js";
import type { DocumentReference } from "../../shared.js";
import { ONLINE_STATUS } from "../../shared.js";
import { PinoModules } from "../../utils/index.js";
import { toLoraEui } from "../../utils/shortCodes/EuiShortCodeMapper.js";
import { TimeOverride } from "../../utils/timeOverrideUtils.js";
import {
    getZodSchemaFromClass,
    ObjectIdString,
    ThirdPartyDeviceTypeString,
    validate,
} from "../../zod-decorator/index.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { ExternalLink } from "../commonSubSchemas/externalLink.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import { ENTITY_NAME_MAX_LENGTH, entityNameZodValidator } from "../commonZodValidators/entityName.js";
import { SensorSpecifier } from "../device/subSchemas/sensorSpecifier.js";
import type { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { ThirdPartyDeviceFieldsWithDefaults, ThirdPartyDeviceSchemaTypes } from "../types/thirdPartyDevice.d.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import { trackMixed } from "../utils/mixedSchemaSavingUtils.js";
import { type DtoValidatorTypes } from "../validatableClass.js";
import { Warning } from "../warning/warning.js";
import * as getters from "./getters.js";

const SCHEMA_KEY = "ThirdPartyDevice" as const;

/** MongoDB collection name for {@link ThirdPartyDevice} documents. */
export const THIRD_PARTY_DEVICE_COLLECTION_NAME = "thirdPartyDevices" as const;

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

/**
 * Generates and assigns a unique verdiId to the third-party device. Registered in the backend
 * (where AssetIdentityModel lives) because verdi-common cannot import backend code.
 */
export const thirdPartyDeviceVerdiIdGenerationPreValidate = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceVerdiIdGenerationPreValidate");

export const generatedSensorClusterSyncPreSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => void
>("thirdPartyDeviceGeneratedSensorClusterSyncPreSave", { synchronous: true });

export const generatedSensorClusterSyncPostSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceGeneratedSensorClusterSyncPostSave");

/**
 * Keeps the WeatherLink poll scheduler set in step with the `davisWeatherStation` registry. Registered
 * in the backend, where the BullMQ queue lives. A no-op for every other device type.
 */
export const weatherLinkSchedulerSyncPostSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceWeatherLinkSchedulerSyncPostSave");

/**
 * Reacts to `syncPaused` and `deleted` changing on a save: the pre hook records which changed (and
 * keeps `deleted` and `syncPaused` consistent), the post hook posts and clears the warnings that go
 * with them. Registered in the backend, where the warning handlers live.
 */
export const syncStateChangePreSave = createDelayedRegistrationHook<(document: DocumentType<ThirdPartyDevice>) => void>(
    "thirdPartyDeviceSyncStateChangePreSave",
    { synchronous: true },
);

export const syncStateChangePostSave = createDelayedRegistrationHook<
    (document: DocumentType<ThirdPartyDevice>) => Promise<void>
>("thirdPartyDeviceSyncStateChangePostSave");

// Generates verdiId on "validate" rather than "save" since verdiId is a required field
@pre<ThirdPartyDevice>("validate", async function (this: DocumentType<ThirdPartyDevice>) {
    if (this.verdiId) return;
    try {
        await thirdPartyDeviceVerdiIdGenerationPreValidate.function(this);
    } catch (e) {
        logger.error({ error: e, thirdPartyDeviceId: this._id }, "could not generate verdiId for third-party device");
        throw e;
    }
})
@pre<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await generatedSensorClusterSyncPreSave.function(this);
})
@pre<ThirdPartyDevice>("save", function (this: DocumentType<ThirdPartyDevice>) {
    syncStateChangePreSave.function(this);
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await syncStateChangePostSave.function(this);
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — device saves must not
    // break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            assetCollection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
            // Unsupported device types reach production before they get a config entry, so this must
            // degrade to the generic third-party type rather than throw and fail the save.
            assetType: DeviceTypeConfigurations[this.deviceType]?.assetType ?? AssetType.THIRD_PARTY_DEVICE,
            // Only loraEui-scheme devices sync a loraEui; the rest sync on verdiId alone. toLoraEui (not the
            // scheme's normalizeInput) preserves the historical behaviour byte-for-byte.
            loraEui:
                (DeviceTypeConfigurations[this.deviceType]?.identifierScheme ?? "loraEui") === "loraEui"
                    ? toLoraEui(this.node_id)
                    : undefined,
            verdiId: this.verdiId,
        })
        .catch((e) =>
            logger.warn({ error: e, verdiId: this.verdiId }, "could not sync asset identity for third-party device"),
        );
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await generatedSensorClusterSyncPostSave.function(this);
})
@post<ThirdPartyDevice>("save", async function (this: DocumentType<ThirdPartyDevice>) {
    await weatherLinkSchedulerSyncPostSave.function(this);
})
@modelOptions({
    schemaOptions: {
        collection: THIRD_PARTY_DEVICE_COLLECTION_NAME,
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@plugin(mongooseAutoPopulate as any)
@post<SchemaBase<ThirdPartyDevice>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ThirdPartyDevice extends SchemaBase<ThirdPartyDevice, ThirdPartyDeviceFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public createdAt?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public updatedAt?: Date;

    @validate({
        childClass: VertexInfo,
    })
    @prop({ type: () => VertexInfo, required: true, _id: false })
    public position!: VertexInfo;

    // Shape is resolved from deviceType's identifierScheme (DeviceTypeConfigurations) rather than a fixed
    // regex, so validation matches whichever scheme the device type declares.
    // Trade-off: the generated OpenAPI schema for node_id degrades from a pattern (e.g. ^eui-[0-9a-f]{16}$)
    // to a bare minLength, since a per-deviceType shape isn't representable in JSON Schema without a
    // discriminated union keyed on a sibling field. Server-side validation is unaffected either way.
    @validate({ zodValidator: z.string().min(1) })
    @prop({
        type: () => String,
        required: true,
        unique: true,
        validate: {
            // `this` is the document under validation, so this has to be a `function` rather than an arrow.
            // parse throws a ZodError on failure; mongoose uses that message as the ValidationError.
            validator: function (this: ThirdPartyDevice, value: string): boolean {
                IdentifierSchemeConfigurations[
                    DeviceTypeConfigurations[this.deviceType]?.identifierScheme ?? "loraEui"
                ].storedFormat.parse(value);
                return true;
            },
        },
    })
    public node_id!: string;

    @validate({ zodValidator: entityNameZodValidator })
    @prop({ type: () => String, required: true, maxlength: ENTITY_NAME_MAX_LENGTH })
    public name!: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    // TODO: Update to actually reflect being Zone References
    @validate({ zodValidator: z.array(ObjectIdString).optional() })
    @prop({ type: () => [mongoose.Types.ObjectId], required: true, default: [] }, PropType.ARRAY)
    public connectedZones: DocumentReference[] = [];

    @validate({
        childClass: SensorSpecifier,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => [SensorSpecifier], required: true, default: [], _id: false }, PropType.ARRAY)
    public connectedSensors: SensorSpecifier[] = [];

    @validate({ zodValidator: ObjectIdString.optional(), responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public _aoi?: DocumentReference;

    @validate({ zodValidator: z.string(), responseOnly: true })
    @prop({ type: () => String, required: true, unique: true })
    public verdiId!: string;

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: "" })
    public adminNotes: string = "";

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: "" })
    public notes: string = "";

    @validate({
        childClass: ExternalLink,
        childClassTransform: (object) => z.record(z.string(), object).optional(),
    })
    @prop({ type: () => Object, required: true, default: {}, allowMixed: Severity.ALLOW })
    public externalLinks: Record<string, ExternalLink> = {};

    @validate({ childClass: Warning, childClassTransform: (object) => z.array(object).optional(), responseOnly: true })
    @prop({
        ref: () => Warning,
        localField: "node_id",
        foreignField: "foreignLookup.targetId",
        match: {
            "foreignLookup.targetCollection": THIRD_PARTY_DEVICE_COLLECTION_NAME,
            versionIsCurrent: true,
            deleted: false,
        },
        autopopulate: true,
    })
    public warnings: Warning[] = [];

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public EnclosureID!: string;

    @validate({ zodValidator: ThirdPartyDeviceTypeString })
    @prop({ type: () => String, required: true })
    public deviceType!: ThirdPartyDeviceType;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastContact?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastPositionChange?: Date;

    /**
     * True while the user has switched this device's data sync off. The device, its clusters and its
     * streams all stay and it remains on the map; only polling stops. Distinct from `deleted`, which
     * clears it on save. A plain field edit: the save hooks stop the scheduler and post the warning.
     */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    public syncPaused: boolean = false;

    /**
     * Soft delete, with the same name and shape the SCD base class uses, ready for this document to be
     * versioned. Default reads exclude a deleted device; its streams, points, reservations and cursors
     * all stay, so reviving it (clearing this flag) brings the same device back with its history.
     */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    /** Raw battery voltage (V) as reported by the device. */
    @validate({
        zodValidator: z.number().optional().describe("Raw battery voltage (V) as reported by the device."),
        responseOnly: true,
    })
    @prop({ type: () => Number, required: false })
    public batteryIndicator?: number;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @trackMixed
    @prop({ type: () => Object, required: true, allowMixed: Severity.ALLOW, default: {} })
    public deviceTypeSpecificInformation: Record<string, any> = {};

    static getters = { ...getters } as const;

    static utilities = {
        /**
         * Returns the online status of the device based on lastContact compared to referenceTime.
         * Uses the per-device-type fallback offline threshold from DeviceTypeConfigurations.
         * Note: to be migrated to a warnings-based approach in the future.
         */
        getOnlineStatus: (self: ThirdPartyDevice, referenceTime: Date = TimeOverride.now()): ONLINE_STATUS => {
            if (!self.lastContact) {
                return ONLINE_STATUS.AwaitingConnection;
            }
            const lastContactTime = new Date(self.lastContact).getTime();
            if (Number.isNaN(lastContactTime)) {
                return ONLINE_STATUS.AwaitingConnection;
            }
            const deviceTypeConfiguration = DeviceTypeConfigurations[self.deviceType];
            if (deviceTypeConfiguration === undefined) {
                console.error(
                    `Unable to calculate offline state for third-party device with unconfigured deviceType "${self.deviceType}"`,
                    { node_id: self.node_id, deviceType: self.deviceType },
                );
                return ONLINE_STATUS.LiveMonitoringNotSupported;
            }
            const { fallbackOfflineThresholdMs } = deviceTypeConfiguration;
            if (referenceTime.getTime() - lastContactTime > fallbackOfflineThresholdMs) {
                return ONLINE_STATUS.Offline;
            }
            return ONLINE_STATUS.Online;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ThirdPartyDeviceSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ThirdPartyDevice, ThirdPartyDeviceFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

/**
 * The decorator system has no object-level hook, so node_id's valid shape — which depends on the sibling
 * deviceType field's identifierScheme — is layered on after generation, over every variant, mirroring the
 * mongoose validator above and the VersionEventActor precedent (versionEventActor.ts). This has to be a
 * `.transform()`, not a `.superRefine()`: it must write the normalized node_id back onto the parsed body,
 * the way the `.toLowerCase()` zod transform it replaces did — callers like the v2 PATCH endpoint persist
 * the parsed body's node_id directly, with no normalization step of their own.
 */
const generatedValidators = getZodSchemaFromClass(ThirdPartyDevice) as unknown as Record<string, z.ZodType>;

ThirdPartyDevice.dtoValidators = Object.fromEntries(
    Object.entries(generatedValidators).map(([variant, validator]) => [
        variant,
        validator.transform((device: { deviceType?: ThirdPartyDeviceType; node_id?: string }, ctx) => {
            const { deviceType, node_id } = device;
            // Nothing to cross-check: a partial update DTO may legitimately omit either field.
            if (deviceType === undefined || node_id === undefined) return device;
            const identifierScheme = DeviceTypeConfigurations[deviceType]?.identifierScheme ?? "loraEui";
            const scheme = IdentifierSchemeConfigurations[identifierScheme];
            const normalizedNodeId = scheme.normalizeInput(node_id);
            if (!scheme.storedFormat.safeParse(normalizedNodeId).success) {
                ctx.addIssue({
                    code: "custom",
                    path: ["node_id"],
                    message: `node_id "${node_id}" does not match the "${identifierScheme}" identifier scheme required by device type "${deviceType}"`,
                });
            }
            return { ...device, node_id: normalizedNodeId };
        }),
    ]),
) as unknown as DtoValidatorTypes<ThirdPartyDeviceSchemaTypes>;
